import { parseEnvelope, type AnyEnvelope, type RecordType } from '@wiztime/core';
import { concatBytes, fromBase64, toBase64, toHex, utf8 } from './bytes';
import { CIPHERTEXT_VERSION, IV_BYTES, KNOWN_RECORD_TYPES } from './constants';
import { DecryptError, DecryptFailure } from './errors';
import type { DerivedKeys } from './keys';

const KNOWN_TYPE_SET: ReadonlySet<string> = new Set(KNOWN_RECORD_TYPES);

/** The encrypted form of one record as exchanged with the server. */
export interface EncryptedRecord {
  /** Opaque record identifier: hex HMAC of "type:id"; 64 characters. */
  readonly rid: string;
  /** The envelope's timestamp, readable by the server for last-writer-wins. */
  readonly hlc: string;
  /** Base64 of 0x01 || IV(12) || AES-GCM(envelope JSON). */
  readonly ciphertext: string;
}

export type RecordKeys = Pick<DerivedKeys, 'encKey' | 'ridKey'>;

const AUTH_TAG_BYTES = 16;

/** `hex(HMAC-SHA256(ridKey, "{type}:{id}"))`: the server never sees record types or identifiers. */
export async function computeRid(ridKey: CryptoKey, type: RecordType, id: string): Promise<string> {
  const mac = await globalThis.crypto.subtle.sign('HMAC', ridKey, utf8(`${type}:${id}`));
  return toHex(new Uint8Array(mac));
}

/** Additional authenticated data: binds a ciphertext to its space, its rid and the format version. */
function aad(spaceId: string, rid: string): Uint8Array<ArrayBuffer> {
  return utf8(`${spaceId}|${rid}|${CIPHERTEXT_VERSION}`);
}

export async function encryptEnvelope(
  keys: RecordKeys,
  spaceId: string,
  envelope: AnyEnvelope,
): Promise<EncryptedRecord> {
  const rid = await computeRid(keys.ridKey, envelope.type, envelope.id);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = await globalThis.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad(spaceId, rid) },
    keys.encKey,
    utf8(JSON.stringify(envelope)),
  );
  const payload = concatBytes(new Uint8Array([CIPHERTEXT_VERSION]), iv, new Uint8Array(sealed));
  return { rid, hlc: envelope.hlc, ciphertext: toBase64(payload) };
}

/**
 * Decrypt and validate one pulled record. Throws `DecryptError` when the ciphertext is malformed,
 * was not produced with this space's key for this rid, or does not hold a valid envelope whose
 * identity and timestamp match the record metadata.
 */
export async function decryptRecord(
  keys: RecordKeys,
  spaceId: string,
  record: EncryptedRecord,
  knownTypes: ReadonlySet<string> = KNOWN_TYPE_SET,
): Promise<AnyEnvelope> {
  const payload = fromBase64(record.ciphertext);
  if (payload === null || payload.length < 1 + IV_BYTES + AUTH_TAG_BYTES) {
    throw new DecryptError(DecryptFailure.Malformed);
  }
  if (payload[0] !== CIPHERTEXT_VERSION) throw new DecryptError(DecryptFailure.UnsupportedVersion);

  let plain: ArrayBuffer;
  try {
    plain = await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: payload.subarray(1, 1 + IV_BYTES), additionalData: aad(spaceId, record.rid) },
      keys.encKey,
      payload.subarray(1 + IV_BYTES),
    );
  } catch {
    throw new DecryptError(DecryptFailure.AuthenticationFailed);
  }

  let json: unknown;
  try {
    json = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(plain));
  } catch {
    throw new DecryptError(DecryptFailure.InvalidEnvelope);
  }
  // Told apart from a corrupt envelope: a newer client's record type is skipped now and fetched again
  // once this device runs a build that knows the type.
  if (
    typeof json === 'object' &&
    json !== null &&
    typeof (json as { type?: unknown }).type === 'string' &&
    !knownTypes.has((json as { type: string }).type)
  ) {
    throw new DecryptError(DecryptFailure.UnknownRecordType);
  }
  const parsed = parseEnvelope(json);
  if (!parsed.success) throw new DecryptError(DecryptFailure.InvalidEnvelope);
  const envelope = parsed.data;
  if (
    envelope.hlc !== record.hlc ||
    (await computeRid(keys.ridKey, envelope.type, envelope.id)) !== record.rid
  ) {
    throw new DecryptError(DecryptFailure.MetadataMismatch);
  }
  return envelope;
}
